import { API_CONFIG, getApiUrl, getAuthHeaders } from './config/api';

export type DangerLevel = 'Low' | 'Moderate' | 'High' | 'Critical';
export const DANGER_LEVELS: DangerLevel[] = ['Low', 'Moderate', 'High', 'Critical'];

export interface Allergy {
  id: number;
  name: string;
  dangerLevel: DangerLevel;
  medications: string[]; // flatten medication list for frontend simplicity
}

export interface PatientAllergy extends Allergy {
  patientId: number;
  notes?: string;
}

const normalizeDangerLevel = (value: number | string): DangerLevel => {
  if (typeof value === 'number') {
    return DANGER_LEVELS[value] ?? 'Low';
  }
  return (DANGER_LEVELS.includes(value as DangerLevel) ? value : 'Low') as DangerLevel;
};

const normalizeMedications = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.map((item: any) => typeof item === 'string' ? item : item?.name ?? '').filter(Boolean);
};

export const AllergyApi = {
  async listCatalog(token: string): Promise<Allergy[]> {
    const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ALLERGIES), { headers: getAuthHeaders(token) });
    if (!res.ok) throw new Error('Failed to load allergies');
    const data = await res.json();
    return Array.isArray(data) ? data.map((a: any) => ({
      id: a.id,
      name: a.name,
      dangerLevel: normalizeDangerLevel(a.dangerLevel),
      medications: normalizeMedications(a.medications)
    })) : [];
  },
  async listForPatient(token: string, patientId: number): Promise<PatientAllergy[]> {
    const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.PATIENT_ALLERGIES, { patientId }), { headers: getAuthHeaders(token) });
    if (!res.ok) throw new Error('Failed to load patient allergies');
    const data = await res.json();
    return Array.isArray(data) ? data.map((a: any) => ({
      id: a.id,
      name: a.name,
      dangerLevel: normalizeDangerLevel(a.dangerLevel),
      medications: normalizeMedications(a.medications),
      patientId: patientId,
      notes: a.notes
    })) : [];
  },
  async addToPatient(token: string, patientId: number, allergyId: number) {
    // Backend expects POST /patients/{patientId}/allergies/{allergyId}
    const url = getApiUrl(`${API_CONFIG.ENDPOINTS.PATIENTS}/${patientId}/allergies/${allergyId}`);
    const res = await fetch(url, { method: 'POST', headers: getAuthHeaders(token) });
    if (!res.ok) throw new Error('Failed to add patient allergy');
  },
  async removeFromPatient(token: string, patientId: number, allergyId: number) {
    const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.PATIENT_ALLERGY_DELETE, { patientId, allergyId }), { method: 'DELETE', headers: getAuthHeaders(token) });
    if (!res.ok) throw new Error('Failed to remove patient allergy');
  }
};
